import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import { COMPANY } from '../mock';

const LAST_UPDATED = 'February 2026';

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-[22px] md:text-[26px] font-semibold text-slate-900 tracking-tight">
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-[15.5px] leading-relaxed text-slate-600">
      {children}
    </div>
  </section>
);

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white text-slate-900 antialiased overflow-x-hidden">
      <Navbar />
      <main className="pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13.5px] text-slate-500 hover:text-slate-900 transition-colors"
            data-testid="privacy-back-home"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>

          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[12px] font-medium text-slate-700 uppercase tracking-[0.18em]">
            Privacy Policy
          </div>

          <h1 className="mt-4 font-display text-[40px] md:text-[52px] leading-[1.05] font-semibold tracking-tight text-slate-900">
            Your data, handled with{' '}
            <span className="bg-gradient-to-r from-violet-500 to-indigo-500 bg-clip-text text-transparent">
              care.
            </span>
          </h1>
          <p className="mt-5 text-[16px] leading-relaxed text-slate-600">
            This Privacy Policy explains how {COMPANY.fullName} ("we", "us" or "our") collects, uses, stores and protects information when you visit our website, contact us or engage our services. Last updated: {LAST_UPDATED}.
          </p>

          <Section title="1. Who we are">
            <p>
              {COMPANY.fullName} is a boutique engineering consultancy based in Melbourne, Australia. We can be reached at{' '}
              <a href={`mailto:${COMPANY.email}`} className="text-violet-600 hover:underline">
                {COMPANY.email}
              </a>
              . We are the data controller for personal information collected through this website.
            </p>
          </Section>

          <Section title="2. Information we collect">
            <p>We collect only the information necessary to respond to enquiries and deliver our services. This may include:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Contact information</strong> you provide voluntarily via our contact form: name, work email, topic and message.</li>
              <li><strong>Communication records</strong>: emails, meeting notes and other correspondence exchanged during an engagement or enquiry.</li>
              <li><strong>Technical information</strong>: basic server logs (IP address, browser type, pages visited) collected automatically for security and site reliability.</li>
            </ul>
            <p>We do not intentionally collect sensitive personal information (such as health, financial or identity documents) through this website.</p>
          </Section>

          <Section title="3. How we use your information">
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Respond to your enquiry and schedule follow-up conversations.</li>
              <li>Deliver, manage and improve the services you have engaged us for.</li>
              <li>Send you occasional service-related updates (only where relevant to an active engagement).</li>
              <li>Comply with our legal, regulatory and contractual obligations.</li>
              <li>Protect our website, systems and users from abuse or fraud.</li>
            </ul>
            <p>We do not sell, rent or trade your personal information, and we do not use it for automated decision-making or profiling.</p>
          </Section>

          <Section title="4. Cookies and analytics">
            <p>
              Our website uses only strictly necessary cookies required for the site to function. If we add analytics or marketing cookies in future, we will update this policy and, where required, request your consent before setting them.
            </p>
          </Section>

          <Section title="5. How we share information">
            <p>We keep your information confidential. We may share it only with:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Trusted service providers</strong> that help us operate our business (for example, cloud hosting, email and CRM tools), under written agreements that require them to protect your data.</li>
              <li><strong>Professional advisers</strong> such as lawyers and accountants, where reasonably necessary.</li>
              <li><strong>Regulators or law enforcement</strong>, where we are legally required to disclose information.</li>
            </ul>
            <p>Where information is transferred outside your country of residence, we take reasonable steps to ensure a comparable level of protection.</p>
          </Section>

          <Section title="6. How long we keep your information">
            <p>
              We retain personal information only for as long as necessary to fulfil the purpose it was collected for, to comply with legal or contractual obligations, or to defend legal claims. Contact enquiries that do not lead to an engagement are typically deleted within 24 months.
            </p>
          </Section>

          <Section title="7. How we protect your information">
            <p>
              We apply industry-standard technical and organisational safeguards, including encryption in transit, access controls, least-privilege permissions and secure development practices. No system is completely secure, but we work continuously to reduce risk.
            </p>
          </Section>

          <Section title="8. Your rights">
            <p>Subject to applicable law, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Request access to the personal information we hold about you.</li>
              <li>Ask us to correct, update or delete your information.</li>
              <li>Object to or restrict certain uses of your information.</li>
              <li>Withdraw consent at any time, where processing is based on consent.</li>
              <li>Lodge a complaint with your local privacy regulator (in Australia, the Office of the Australian Information Commissioner).</li>
            </ul>
            <p>
              To exercise any of these rights, email us at{' '}
              <a href={`mailto:${COMPANY.email}`} className="text-violet-600 hover:underline">
                {COMPANY.email}
              </a>
              . We will respond within a reasonable time and free of charge.
            </p>
          </Section>

          <Section title="9. Children">
            <p>
              Our services are not directed to children under 16 and we do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us so we can delete it.
            </p>
          </Section>

          <Section title="10. Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology or legal requirements. When we do, we will revise the "Last updated" date at the top of this page. Material changes will be communicated more prominently.
            </p>
          </Section>

          <Section title="11. Contact us">
            <p>
              Questions, requests or complaints about this policy can be sent to:
            </p>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 text-[14.5px]">
              <div className="font-semibold text-slate-900">{COMPANY.fullName}</div>
              <div className="mt-1 text-slate-600">Level 27, 101 Collins St, Melbourne VIC 3000, Australia</div>
              <div className="mt-1">
                <a href={`mailto:${COMPANY.email}`} className="text-violet-600 hover:underline">
                  {COMPANY.email}
                </a>
              </div>
            </div>
          </Section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
